import { Badge } from "@/components/ui/baseUi/badge"
import { Counter } from "@/components/ui/baseUi/counter"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { CoachOfferEntity } from "@modules/coachOffers/entities/coachOfferEntity"
import { CoachOfferType } from "@modules/coachOffers/entities/coachOfferTypes"
import { word } from "@modules/core/utils/i18n"
import { CircleIcon, CirclePileIcon, LockIcon, LockOpenIcon, UsersRoundIcon } from "lucide-react"

type CoachOfferAccompaniedCardProps = {
  offer: CoachOfferEntity
} & React.ComponentProps<"div">

export function CoachOfferAccompaniedCard({ offer, className, ...props }: CoachOfferAccompaniedCardProps) {
  const rules = offer.getReservationRulesText()

  return (
    <div
      className={cn(
        "flex w-full cursor-pointer justify-between gap-4 rounded-xl border border-surface-border-gray-default p-4",
        className,
      )}
      {...props}
    >
      <div className="flex w-7/12 flex-col justify-between gap-3">
        <div className="flex flex-col">
          <Text fontStyle="TM" className="leading-none wrap-anywhere text-textOnSurface-default">
            {offer.name}
          </Text>
          {rules.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {rules.map((rule, index) => (
                <Text key={index} fontStyle="LS" className="px-1 text-(--badge-primary-text-subtle) italic">
                  {rule}
                </Text>
              ))}
            </div>
          )}
        </div>

        {offer.showcaseDisplay ? (
          <div className="flex flex-row items-center gap-1">
            <LockOpenIcon size={16} />
            <Text fontStyle="BS" className="leading-normal text-textOnSurface-default">
              {word("coach.offers.public")}
            </Text>
          </div>
        ) : (
          <div className="flex flex-row items-center gap-1">
            <LockIcon size={16} />
            <Text fontStyle="BS" className="leading-normal text-textOnSurface-default">
              {word("coach.offers.private")}
            </Text>
          </div>
        )}

        {offer.paymentOffers.length > 0 && (
          <Badge variant="primarySubtle" size="lg">
            <Text fontStyle="LM" className="leading-normal">
              {`${offer.paymentOffers.length.toString()} ${word("coach.offers.card.payment.options", { count: offer.paymentOffers.length })}`}
            </Text>
          </Badge>
        )}
      </div>

      <div className="flex flex-col items-end gap-1">
        <Badge variant="primaryIntense" size="lg">
          {getTypeIcon(offer.type)}
          {getTypeLabel(offer.type)}
        </Badge>
        <Badge size="md" variant="neutralSubtle">
          <UsersRoundIcon size={16} />
          {/* TODO NST : end this part */}
          <Text fontStyle="LM" className="text-textOnSurface-muted">
            ???
          </Text>
          <Counter variant="negativeSubtle" size="sm">
            ???
          </Counter>
        </Badge>
      </div>
    </div>
  )
}

function getTypeIcon(type: CoachOfferType) {
  switch (type) {
    case CoachOfferType.Individual:
      return <CircleIcon size={16} />
    case CoachOfferType.Collective:
      return <CirclePileIcon size={16} />
  }
}

function getTypeLabel(type: CoachOfferType) {
  switch (type) {
    case CoachOfferType.Individual:
      return word("coach.offers.type.INDIVIDUAL")
    case CoachOfferType.Collective:
      return word("coach.offers.type.COLLECTIVE")
  }
}
